import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Customer } from '../../interfaces/Customer';
import { NeomyticConsultantID } from '../../interfaces/Consultant';
import { customersActionCreators } from '../../store/customers';
import { ArrowClockwiseIcon, EditIcon, GroupIcon, useEditorStyles, useTabStyles } from '../Styles/Editor';
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, InputOnChangeData, Label, SearchBox, SearchBoxChangeEvent, Skeleton, SkeletonItem, Table, TableBody, TableCell, TableCellActions, TableCellLayout, TableHeader, TableHeaderCell, TableRow } from '@fluentui/react-components';
import { useCustomers, useRigths } from '../Tools/ObjectLoader';

type CustomersTabProps = {
    ForceLoggedInConsultant: boolean;
};
export const CustomersTab: React.FC<CustomersTabProps> = ({ ForceLoggedInConsultant }) => {
    const { customers } = useCustomers();
    const [filter, setFilter] = React.useState<string | null>(null);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const stylesTab = useTabStyles();
    const [customersToDisplay, setCustomersToDisplay] = React.useState<Customer[]>();
    const { isAdmin, consultantid } = useRigths();

    React.useEffect(() => {
        if (customers && consultantid) {
            if (ForceLoggedInConsultant) {
                setCustomersToDisplay(customers.filter(x => x.consultantid === consultantid || x.sharedWithAllConsultants));
            }
            else {
                setCustomersToDisplay(customers.filter(x => x.consultantid.toUpperCase() === NeomyticConsultantID));
            }
        }
    }, [customers, ForceLoggedInConsultant, consultantid]);

    const custok: Customer[] = customersToDisplay ?? [];
    const custs = custok && filter && filter.length != 0
        ? custok.filter(t => (t.displayName && t.displayName.toLowerCase().includes(filter.toLowerCase())) || (t.name && t.name.toLowerCase().includes(filter.toLowerCase())))
        : custok;
    const custsActive = custs.filter(t => t.isActif);
    const custsNoActive = custs.filter(t => !t.isActif);

    const columns = [
        { columnKey: "DisplayName", label: t('Customer') },
        { columnKey: "IsAdmin", label: t('Address') },
        { columnKey: "CanEnterTimeSheet", label: t("VAT") },
    ];

    const mapCustomer = (c: Customer): JSX.Element => (
        <TableRow key={c.customerid}>
            <TableCell>
                <TableCellLayout>
                    <Button appearance="transparent" className={stylesTab.TestButton} onClick={() => dispatch(customersActionCreators.requestCustomerQuickView(c))}>{c.displayName}</Button>
                </TableCellLayout>
                <TableCellActions>
                    {(isAdmin || (c.consultantid === consultantid)) &&
                        <Button
                            icon={<EditIcon />}
                            appearance="subtle"
                            aria-label={t('Edit')}
                            onClick={() => dispatch(customersActionCreators.requestCustomerEdition(c))}
                        />
                    }
                    {!ForceLoggedInConsultant &&
                        <Button
                            icon={<GroupIcon />}
                            appearance="subtle"
                            aria-label={t('People')}
                            onClick={() => dispatch(customersActionCreators.requestCustomerContactsEdition(c))}
                        />
                    }
                </TableCellActions>
            </TableCell>
            <TableCell>
                <div className={stylesTab.List}>
                    <Label>{c.name}</Label>
                    <Label>{c.address}</Label>
                    {c.address2 && <Label>{c.address2}</Label>}
                    {c.address3 && <Label>{c.address3}</Label>}
                    <Label>{c.postalCode + " " + c.city}</Label>
                    <Label>{c.country}</Label>
                </div>
            </TableCell>
            <TableCell>{c.vat}</TableCell>
        </TableRow>
    );

    const tableHeader =
        <TableHeader>
            <TableRow>
                {columns.map((column) => (
                    <TableHeaderCell key={column.columnKey}>
                        {column.label}
                    </TableHeaderCell>
                ))}
            </TableRow>
        </TableHeader>;

    return <div className={stylesTab.rootSub}>
        <div className={stylesTab.MainBar}>
            <SearchBox
                placeholder={t('Contains')}
                onChange={(e: SearchBoxChangeEvent, data: InputOnChangeData) => { setFilter(data.value); }}
                dismiss={{ onClick: () => { setFilter(null); } }}
                className={styles.fluid}
            />
            <div className={stylesTab.MarginLeftAuto}>
                {/*<Button icon={<AddIcon />} shape="circular" title={t('Add')}*/}
                {/*    onClick={() => {*/}
                {/*        const c: Customer = {*/}
                {/*            customerid: null,*/}
                {/*            displayName: null,*/}
                {/*            name: null,*/}
                {/*            address: null,*/}
                {/*            address2: null,*/}
                {/*            address3: null,*/}
                {/*            postalCode: null,*/}
                {/*            city: null,*/}
                {/*            country: null,*/}
                {/*            vat: null,*/}
                {/*            vatexempt: false,*/}
                {/*            vatcode: 21,*/}
                {/*            contacts: null,*/}
                {/*            defaultLanguage: null,*/}
                {/*            paymentTerms: 30,*/}
                {/*            isActif: true,*/}
                {/*            peppolIdentifier: null,*/}
                {/*            countryCode: null,*/}
                {/*            consultantid: (ForceLoggedInConsultant && consultantid ? consultantid : NeomyticConsultantID), // neomytic consultant*/}
                {/*            sharedWithAllConsultants: false,*/}
                {/*        };*/}
                {/*        dispatch(customersActionCreators.requestCustomerEdition(c));*/}
                {/*    }} />*/}
                <Button icon={<ArrowClockwiseIcon />} shape="circular" title="Reload"
                    onClick={() => dispatch(customersActionCreators.ReloadCustomer())}
                />
            </div>
        </div>
        <Table arial-label="Default table">
            {tableHeader}
            <TableBody>
                {custs === undefined
                    ? [...Array(10)].map((_, index) =>
                        <TableRow key={index}>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                        </TableRow>)
                    : custsActive.map(c => mapCustomer(c))
                }
            </TableBody>
        </Table>
        {
            custsNoActive && custsNoActive.length > 0 &&
            <Accordion collapsible>
                <AccordionItem value="1">
                    <AccordionHeader><h2>{t('Obsolete')}</h2></AccordionHeader>
                    <AccordionPanel>
                        <Table arial-label="Default table">
                            {tableHeader}
                            <TableBody>
                                {custsNoActive.map(c => mapCustomer(c))}
                            </TableBody>
                        </Table>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        }
    </div>;
};